/**
 * Los datos de la cuenta que ve el agente de soporte al abrir la conversación.
 *
 * Vive aparte del adaptador del proveedor porque es lo único de todo esto que es
 * puro: entran el usuario y su facturación, sale un objeto. Se puede testear sin
 * DOM y sin script de terceros, que es justo lo que no se puede hacer con el
 * resto de la carpeta.
 */

/** Normaliza una fecha (ISO string o epoch en s/ms) a formato español "DD/MM/AAAA". */
export function fmtDate(v) {
  if (!v) return null;
  const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(v);
  if (Number.isNaN(d.getTime())) return typeof v === 'string' ? v : null;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Atributos de la sesión para el panel de soporte.
 *
 * Se omiten los vacíos a propósito: un atributo en blanco en la ficha del cliente
 * es peor que su ausencia, porque parece un dato y no lo es.
 *
 * Las fechas van en "DD/MM/AAAA" y no en ISO porque las lee una persona, no una
 * máquina, y es el formato que el equipo ya tiene interiorizado.
 *
 * @param {{ createdAt?: string | number } | null} user
 * @param {{
 *   isPro?: boolean, adminAssignedPro?: boolean, proSince?: string | number | null,
 *   purchasedSeats?: number, activeLocationIds?: string[],
 *   subscriptionStatus?: string | null, currentPeriodEnd?: string | number | null,
 *   trialEnd?: string | number | null,
 * } | null} billing
 * @returns {Record<string, string | number>}
 */
export function buildSupportTraits(user, billing) {
  /** @type {Record<string, string | number>} */
  const traits = {};
  const add = (key, value) => {
    if (value === null || value === undefined || value === '') return;
    traits[key] = value;
  };

  add('registered_since', fmtDate(user?.createdAt));

  if (billing) {
    // plan: "free" (sin Pro o free) · "pro" (de pago) · "pro (NP)" = Pro No
    // Pagado (asignado por admin, sin Stripe).
    let plan = 'free';
    if (billing.isPro) plan = billing.adminAssignedPro ? 'pro (NP)' : 'pro';
    add('plan', plan);
    add('pro_since', fmtDate(billing.proSince));
    add('seats_purchased', billing.purchasedSeats);
    // Los asientos REPARTIDOS. Contar los locales activos mandaba a soporte un
    // "5 comprados, 20 usados" que no significa nada desde que activar es
    // gratis: lo que ocupa asiento es el Pro, no la visibilidad.
    //
    // Y solo con plan: a un free el backend le «asienta» su primer local como
    // local principal, y soporte veía «1 usado» en una cuenta free. Es la poda
    // de `businessLocationIds`, escrita aquí para no importar el store en un
    // módulo puro.
    add('seats_used', Array.isArray(billing.seatedLocationIds)
      ? (billing.isPro ? billing.seatedLocationIds.length : 0) : null);
    add('subscription_status', billing.subscriptionStatus);
    add('current_period_end', fmtDate(billing.currentPeriodEnd));
    add('trial_end', fmtDate(billing.trialEnd));
  }

  return traits;
}

/**
 * Identidad del usuario para el proveedor de chat. Separada de los traits porque
 * son campos que casi cualquier proveedor trata de forma especial (los indexa,
 * los usa para deduplicar contactos), no atributos libres.
 *
 * El id sale de `user.id || user.email`, el mismo patrón que ya usan Sentry y
 * PostHog en `lib/AuthContext.jsx`.
 */
export function buildSupportIdentity(user) {
  if (!user) return null;
  const id = user.id || user.email;
  if (!id) return null;
  const { firstName, lastName } = splitName(user.username);
  return {
    id: String(id),
    email: user.email || null,
    // Richpanel no tiene un campo `name`: lee `firstName` y `lastName`, y un
    // `name` suelto se queda sin mostrar. Por eso el messenger pedía el nombre
    // aunque ya se le hubiera mandado.
    firstName,
    lastName,
    // Ya viene en E.164 (`+34612345678`): lo normaliza el registro.
    phone: user.phone || null,
  };
}

/**
 * `'Ana María López'` → `{ firstName: 'Ana', lastName: 'María López' }`.
 *
 * El mismo corte que hace el servidor para la identidad cifrada
 * (`QR/api/support_identity.py`), para que las dos digan lo mismo.
 */
export function splitName(name) {
  const limpio = (name || '').trim();
  if (!limpio) return { firstName: null, lastName: null };
  const i = limpio.indexOf(' ');
  if (i === -1) return { firstName: limpio, lastName: null };
  return { firstName: limpio.slice(0, i), lastName: limpio.slice(i + 1).trim() || null };
}
